import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
} from "react";
import type {
  AudienceFieldType,
  AudienceFilterInputType,
} from "@/types/graphql/partner.generated";

export interface AudienceEditorValue {
  viewersEnabled: boolean;
  audience: AudienceFilterInputType;
  contactListIds: string[];
}

export interface AudienceEditorProps {
  value: AudienceEditorValue;
  onChange: (patch: Partial<AudienceEditorValue>) => void;
  disabled: boolean;
  /** Оценка числа адресатов; null скрывает строку оценки. */
  estimate: number | null;
  /**
   * false: только зрители платформы по фильтру, без переключателя зрителей
   * и без списков контактов (цепочка, правило сервера contactLists).
   * По умолчанию true.
   */
  allowContactLists?: boolean;
}

export interface ConditionFilterBuilderProps {
  value: AudienceFilterInputType;
  onChange: (value: AudienceFilterInputType) => void;
  disabled: boolean;
  /** Письма выше шага условия: варианты для EMAIL_OPENED и EMAIL_CLICKED. */
  emailNodes: { id: string; label: string }[];
  /**
   * Поля только для шага CONDITION из правил сервера
   * (conditionFieldsOf(rules)); без них только поля аудитории (фильтр
   * правила выхода).
   */
  conditionFields?: readonly AudienceFieldType[];
}

export interface ContentsPickerProps {
  label: string;
  value: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
  /** Предел числа выбранных видео из правил сервера (limits.triggerContents). */
  max?: number;
}

export interface EmailTemplatePickerProps {
  value: string | null;
  /** name: название выбранного шаблона (null при очистке поля). */
  onChange: (id: string | null, name: string | null) => void;
  disabled: boolean;
  label: string;
  error?: boolean;
  helperText?: string | null;
}

export interface EmailTemplatePreviewProps {
  emailTemplateId: string | null;
}

/**
 * Куски других фич, которые нужны рассылкам и автоматизациям. Фича не
 * импортирует другую фичу (FSD): компоненты собирает страница
 * (pages/broadcasts, pages/automations) и кладёт в этот контекст.
 */
export interface MarketingComponents {
  AudienceEditor: FC<AudienceEditorProps>;
  EmailTemplatePicker: FC<EmailTemplatePickerProps>;
  EmailTemplatePreview: FC<EmailTemplatePreviewProps>;
  /** Цепочки (фаза 4): страница рассылок их не подставляет. */
  ConditionFilterBuilder?: FC<ConditionFilterBuilderProps>;
  ContentsPicker?: FC<ContentsPickerProps>;
}

const MarketingComponentsContext = createContext<MarketingComponents | null>(
  null
);

export const MarketingComponentsProvider: FC<
  PropsWithChildren<{ value: MarketingComponents }>
> = ({ value, children }) => (
  <MarketingComponentsContext.Provider value={value}>
    {children}
  </MarketingComponentsContext.Provider>
);

export function useMarketingComponents(): MarketingComponents {
  const value = useContext(MarketingComponentsContext);
  if (!value) {
    throw new Error(
      "MarketingComponentsProvider is missing: the page must provide marketing components"
    );
  }
  return value;
}
